import { useState } from 'react';
import { Segmented } from 'antd';
import type { TableProps } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { Siren } from 'lucide-react';

import { DataTable, MenuThaoTac, Tile, TileHeader } from '../../../components/shared';
import { Badge, Button, Empty, Select } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { KhoaDich } from '../../../i18n';
import { ngayGioVN, soVN } from '../../../lib/dinh-dang';
import { useNhanNguoi } from '../../nhat-ky/components/NhanSuKien';
import { MUC_DO_THONG_BAO, TRANG_THAI_CANH_BAO } from '../constants';
import { useCanhBaoAudit, useXacNhanCanhBao } from '../hooks/useCanhBaoAudit';
import { BO_LOC_TRONG, coBoLoc } from '../nghiep-vu/canh-bao-audit';
import { CAC_MUC } from '../nghiep-vu/quy-tac-canh-bao';
import type { BoLocCanhBao, CanhBaoAudit, MucDoThongBao, TrangThaiCanhBao } from '../types';

/** Giá trị "không lọc" của hai ô lọc — antd không nhận `null` làm `value` của lựa chọn. */
const TAT_CA = 'ALL';

/**
 * "Cảnh báo đã kích hoạt" ở thẻ Nhật ký (ADR-0059 mục 4.1): cảnh báo audit-service
 * tạo khi một quy tắc của `audit.alert_rules` đạt ngưỡng.
 *
 * Lọc mức và trạng thái ở server. "Đánh dấu đã xem" ẩn khi không có quyền sửa
 * hoặc cảnh báo đã được xem (ADR-UI-0005); thao tác này luỹ đẳng và không xoá
 * gì nên không cần hộp xác nhận.
 */
export function KhoiCanhBaoAudit({ tenantId, duocSua }: { tenantId: string; duocSua: boolean }) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const [boLoc, setBoLoc] = useState<BoLocCanhBao>(BO_LOC_TRONG);
  const q = useCanhBaoAudit(tenantId, boLoc);
  const xacNhan = useXacNhanCanhBao(tenantId);
  const [dangXacNhan, setDangXacNhan] = useState<CanhBaoAudit | null>(null);
  const nhanNguoi = useNhanNguoi();

  const loiTaiDau = q.error && !q.data;
  const dangLoc = coBoLoc(boLoc);

  const nhom = (row: CanhBaoAudit) => {
    const nhan = t(`quyTacCanhBao.nhom.${row.group_by}` as KhoaDich);
    if (row.group_by === 'user' && row.group_key !== '*') {
      const nguoi = nhanNguoi(row.group_name, row.group_key);
      return (
        <div className="flex min-w-0 flex-col">
          <span className="text-2xs text-content-muted">{nhan}</span>
          <span className="truncate text-xs text-content-primary" title={nguoi.title}>
            {nguoi.nhan}
          </span>
        </div>
      );
    }
    return row.group_by === 'tenant' || row.group_key === '*' ? (
      <span className="text-xs text-content-secondary">{nhan}</span>
    ) : (
      <div className="flex min-w-0 flex-col">
        <span className="text-2xs text-content-muted">{nhan}</span>
        <span className="truncate font-mono text-xs text-content-primary" title={row.group_key}>
          {row.group_key}
        </span>
      </div>
    );
  };

  const cot: TableProps<CanhBaoAudit>['columns'] = [
    {
      title: t('canhBaoAudit.cotThoiDiem'),
      dataIndex: 'created_at',
      width: 150,
      render: (v: string) => <span className="whitespace-nowrap text-xs text-content-muted">{ngayGioVN(v, locale)}</span>,
    },
    {
      title: t('canhBaoAudit.cotQuyTac'),
      width: 220,
      render: (_v, row) => (
        <div className="flex min-w-0 max-w-xs flex-col">
          <span className="truncate text-sm font-medium text-content-primary">{row.rule_name}</span>
          <span className="truncate font-mono text-2xs text-content-muted">{row.rule_id}</span>
        </div>
      ),
    },
    {
      title: t('canhBaoAudit.cotMuc'),
      dataIndex: 'severity',
      width: 120,
      render: (v: MucDoThongBao) => (
        <Badge variant={MUC_DO_THONG_BAO[v]?.variant ?? 'neutral'}>
          {MUC_DO_THONG_BAO[v] ? t(MUC_DO_THONG_BAO[v].khoa) : v}
        </Badge>
      ),
    },
    {
      title: t('canhBaoAudit.cotNhom'),
      width: 200,
      render: (_v, row) => nhom(row),
    },
    {
      title: t('canhBaoAudit.cotSoLan'),
      width: 110,
      align: 'right',
      render: (_v, row) => (
        <span className="whitespace-nowrap text-xs tabular-nums text-content-secondary">
          {t('canhBaoAudit.soLanTrenNguong', {
            so: soVN(row.event_count, locale),
            nguong: soVN(row.threshold, locale),
          })}
        </span>
      ),
    },
    {
      title: t('canhBaoAudit.cotCuaSo'),
      width: 260,
      render: (_v, row) => (
        <span className="text-xs text-content-muted">
          {t('canhBaoAudit.tuDen', {
            tu: ngayGioVN(row.window_start, locale),
            den: ngayGioVN(row.window_end, locale),
          })}
        </span>
      ),
    },
    {
      title: t('chung.trangThai'),
      dataIndex: 'status',
      width: 130,
      render: (v: TrangThaiCanhBao, row) => (
        <div className="flex flex-col items-start gap-0.5">
          <Badge variant={TRANG_THAI_CANH_BAO[v]?.variant ?? 'neutral'}>
            {TRANG_THAI_CANH_BAO[v] ? t(TRANG_THAI_CANH_BAO[v].khoa) : v}
          </Badge>
          {row.acknowledged_at && (
            <span className="text-2xs text-content-muted">{ngayGioVN(row.acknowledged_at, locale)}</span>
          )}
        </div>
      ),
    },
    ...(duocSua
      ? [
          {
            title: t('chung.thaoTac'),
            width: 72,
            align: 'center' as const,
            render: (_v: unknown, row: CanhBaoAudit) =>
              row.status === 'OPEN' ? (
                <MenuThaoTac
                  tenBanGhi={row.rule_name}
                  muc={[
                    {
                      khoa: 'da-xem',
                      nhan: t('canhBaoAudit.danhDauDaXem'),
                      icon: <CheckOutlined />,
                      onChon: () => {
                        setDangXacNhan(row);
                        xacNhan.mutate(row);
                      },
                    },
                  ]}
                />
              ) : null,
          },
        ]
      : []),
  ];

  const boLocMuc = (
    <Select<string>
      size="small"
      className="w-40"
      aria-label={t('canhBaoAudit.locMuc')}
      value={boLoc.severity ?? TAT_CA}
      options={[
        { value: TAT_CA, label: t('canhBaoAudit.moiMuc') },
        ...CAC_MUC.map((m) => ({ value: m, label: t(MUC_DO_THONG_BAO[m].khoa) })),
      ]}
      onChange={(v) => setBoLoc((b) => ({ ...b, severity: v === TAT_CA ? null : (v as MucDoThongBao) }))}
    />
  );
  const boLocTrangThai = (
    <Segmented<string>
      size="small"
      aria-label={t('canhBaoAudit.locTrangThai')}
      value={boLoc.status ?? TAT_CA}
      options={[
        { value: TAT_CA, label: t('canhBaoAudit.tatCa') },
        { value: 'OPEN', label: t('canhBaoAudit.trangThai.OPEN') },
        { value: 'ACKNOWLEDGED', label: t('canhBaoAudit.trangThai.ACKNOWLEDGED') },
      ]}
      onChange={(v) => setBoLoc((b) => ({ ...b, status: v === TAT_CA ? null : (v as TrangThaiCanhBao) }))}
    />
  );

  return (
    <Tile className="overflow-hidden">
      <TileHeader icon={<Siren size={16} />} tone="primary" title={t('canhBaoAudit.tieuDe')} />
      <div className="flex flex-wrap items-center gap-3 border-b border-default px-5 py-3">
        {boLocMuc}
        {boLocTrangThai}
      </div>
      {xacNhan.error && dangXacNhan && (
        <div role="alert" className="flex flex-wrap items-center justify-between gap-3 border-b border-default bg-danger-50 px-5 py-2.5">
          <span className="min-w-0 text-xs text-danger-700">
            {t('canhBaoAudit.loiXacNhan', { ten: dangXacNhan.rule_name, loi: dichLoi(xacNhan.error) })}
          </span>
          <Button variant="outline" size="sm" onClick={() => void q.refetch()}>
            {t('canhBaoAudit.taiLai')}
          </Button>
        </div>
      )}
      {loiTaiDau ? (
        <div role="alert" className="flex flex-wrap items-center justify-between gap-3 px-5 py-4">
          <span className="min-w-0 text-sm text-danger-700">{t('canhBaoAudit.loi', { loi: dichLoi(q.error) })}</span>
          <Button variant="outline" size="sm" onClick={() => void q.refetch()}>
            {t('chung.thuLai')}
          </Button>
        </div>
      ) : (
        <>
          <DataTable<CanhBaoAudit>
            caption={t('canhBaoAudit.caption')}
            rowKey="id"
            dataSource={q.dong}
            columns={cot}
            loading={q.isLoading}
            size="small"
            pagination={false}
            locale={{
              emptyText: q.isLoading ? (
                ' '
              ) : dangLoc ? (
                <Empty icon={Siren} title={t('canhBaoAudit.rongLoc')} />
              ) : (
                <Empty icon={Siren} title={t('canhBaoAudit.rong')} description={t('canhBaoAudit.rongMo')} />
              ),
            }}
          />
          {q.isFetchNextPageError && (
            <p role="alert" className="border-t border-default px-5 py-2.5 text-xs text-danger-700">
              {t('canhBaoAudit.loiTaiThem', { loi: dichLoi(q.error) })}
            </p>
          )}
          {q.hasNextPage && (
            <div className="flex justify-center border-t border-default px-4 py-3">
              <Button variant="outline" size="sm" loading={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
                {q.isFetchNextPageError ? t('chung.thuLai') : t('chung.taiThem')}
              </Button>
            </div>
          )}
        </>
      )}
    </Tile>
  );
}
